import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Verificar se o token é válido
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setError('Token de redefinição não fornecido');
        return;
      }

      try {
        setLoading(true);
        const response = await axios.post('/api/auth/verify-reset-token', { token });
        
        if (!response.data.valid) {
          setTokenValid(false);
          setError('Token inválido ou expirado');
        }
      } catch (error) {
        console.error('Erro na verificação do token:', error);
        setTokenValid(false);
        setError(error.response?.data?.message || 'Token inválido ou expirado');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação básica
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post('/api/auth/reset-password', {
        token,
        password
      });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success('Senha redefinida com sucesso!');
        // Redirecionar para login após 3 segundos
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Erro ao redefinir senha');
      }
    } catch (error) {
      console.error('Erro na redefinição de senha:', error);
      setError(error.response?.data?.message || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (loading && !tokenValid) {
    return (
      <div className="page page-center">
        <div className="container container-tight py-4">
          <div className="text-center mb-4">
            <a href="/" className="navbar-brand navbar-brand-autodark">
              <img src="https://preview.tabler.io/static/logo.svg" height="36" alt="" />
            </a>
          </div>
          <div className="card card-md">
            <div className="card-body text-center py-4 p-sm-5">
              <div className="spinner-border text-primary mb-3" role="status"></div>
              <h1 className="mt-5">Verificando token...</h1>
              <p className="text-muted">Por favor, aguarde enquanto verificamos seu token de redefinição.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="page page-center">
        <div className="container container-tight py-4">
          <div className="text-center mb-4">
            <a href="/" className="navbar-brand navbar-brand-autodark">
              <img src="https://preview.tabler.io/static/logo.svg" height="36" alt="" />
            </a>
          </div>
          <div className="card card-md">
            <div className="card-body text-center py-4 p-sm-5">
              <span className="avatar avatar-xl mb-3 bg-danger-lt">
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-alert-triangle" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 9v2m0 4v.01"></path>
                  <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"></path>
                </svg>
              </span>
              <h1 className="mt-5">Link inválido</h1>
              <p className="text-muted">{error || 'O link de redefinição de senha é inválido ou expirou.'}</p>
              <div className="mt-4">
                <Link to="/forgot-password" className="btn btn-primary w-100">
                  Solicitar novo link
                </Link>
              </div>
            </div>
          </div>
          <div className="text-center text-muted mt-3">
            Lembrou sua senha? <Link to="/login" tabIndex="-1">Faça login</Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page page-center">
        <div className="container container-tight py-4">
          <div className="text-center mb-4">
            <a href="/" className="navbar-brand navbar-brand-autodark">
              <img src="https://preview.tabler.io/static/logo.svg" height="36" alt="" />
            </a>
          </div>
          <div className="card card-md">
            <div className="card-body text-center py-4 p-sm-5">
              <span className="avatar avatar-xl mb-3 bg-success-lt">
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-check" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M5 12l5 5l10 -10"></path>
                </svg>
              </span>
              <h1 className="mt-5">Senha redefinida com sucesso!</h1>
              <p className="text-muted">Sua senha foi redefinida com sucesso. Você será redirecionado para a página de login em alguns segundos.</p>
              <div className="mt-4">
                <Link to="/login" className="btn btn-primary w-100">
                  Ir para Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-center">
      <div className="container container-tight py-4">
        <div className="text-center mb-4">
          <a href="/" className="navbar-brand navbar-brand-autodark">
            <img src="https://preview.tabler.io/static/logo.svg" height="36" alt="" />
          </a>
        </div>
        <form className="card card-md" onSubmit={handleSubmit}>
          <div className="card-body">
            <h2 className="card-title text-center mb-4">Redefinir senha</h2>
            <p className="text-muted mb-4">Digite sua nova senha abaixo</p>
            
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            
            <div className="mb-3">
              <label className="form-label">Nova senha</label>
              <div className="input-group">
                <input 
                  type={showPassword ? "text" : "password"}
                  className="form-control" 
                  placeholder="Nova senha" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="input-group-text cursor-pointer" onClick={togglePasswordVisibility}>
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M3 3l18 18" />
                      <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" />
                      <path d="M9.363 5.365a9.466 9.466 0 0 1 2.637 -.365c4 0 7.333 2.333 10 7c-.778 1.361 -1.612 2.524 -2.503 3.488m-2.14 1.861c-1.631 1.1 -3.415 1.651 -5.357 1.651c-4 0 -7.333 -2.333 -10 -7c1.369 -2.395 2.913 -4.175 4.632 -5.341" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M12 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                      <path d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333 -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7" />
                    </svg>
                  )}
                </span>
              </div>
              <div className="form-hint">
                A senha deve ter pelo menos 6 caracteres
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Confirmar nova senha</label>
              <div className="input-group">
                <input 
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control" 
                  placeholder="Confirmar nova senha" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <span className="input-group-text cursor-pointer" onClick={toggleConfirmPasswordVisibility}>
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M3 3l18 18" />
                      <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" />
                      <path d="M9.363 5.365a9.466 9.466 0 0 1 2.637 -.365c4 0 7.333 2.333 10 7c-.778 1.361 -1.612 2.524 -2.503 3.488m-2.14 1.861c-1.631 1.1 -3.415 1.651 -5.357 1.651c-4 0 -7.333 -2.333 -10 -7c1.369 -2.395 2.913 -4.175 4.632 -5.341" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                      <path d="M12 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
                      <path d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333 -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7" />
                    </svg>
                  )}
                </span>
              </div>
            </div>
            <div className="form-footer">
              <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processando...
                  </>
                ) : 'Redefinir senha'}
              </button>
            </div>
          </div>
        </form>
        <div className="text-center text-muted mt-3">
          Lembrou sua senha? <Link to="/login" tabIndex="-1">Faça login</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 